export type StudioSettingsWriteOperation =
  | 'settings-overlay'
  | 'generation-authority'

export type StudioSettingsWriteOutcome =
  | { state: 'idle' }
  | { state: 'submitting'; operation: StudioSettingsWriteOperation }
  | {
      state: 'accepted'
      operation: StudioSettingsWriteOperation
      observedAt: string
    }
  | {
      state: 'protected-write-rejected'
      operation: StudioSettingsWriteOperation
      reasonCode: string
      message: string
    }
  | {
      state: 'error'
      operation: StudioSettingsWriteOperation
      message: string
    }

type WorkerRequestFailure = Error & { status: unknown; body: unknown }

const UNSUPPORTED_SETTINGS_KEY_BODY = 'Unsupported settings key'
const GENERATION_AUTHORITY_IMMUTABLE_CODE = 'generation-authority-immutable'

let snapshot: StudioSettingsWriteOutcome = Object.freeze({ state: 'idle' })
const subscribers = new Set<() => void>()
let latestAttempt = 0

function isExactRecord(
  value: unknown,
  keys: readonly string[],
): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const actualKeys = Object.keys(value)
  return (
    actualKeys.length === keys.length &&
    keys.every((key) => Object.prototype.hasOwnProperty.call(value, key))
  )
}

function isWorkerRequestFailure(error: unknown): error is WorkerRequestFailure {
  return (
    error instanceof Error &&
    error.name === 'WorkerRequestError' &&
    typeof (error as Partial<WorkerRequestFailure>).status === 'number'
  )
}

export function classifyStudioSettingsWriteFailure(
  error: unknown,
  operation: StudioSettingsWriteOperation,
): Extract<
  StudioSettingsWriteOutcome,
  { state: 'protected-write-rejected' | 'error' }
> {
  if (
    isWorkerRequestFailure(error) &&
    error.status === 400 &&
    isExactRecord(error.body, ['error', 'key']) &&
    error.body.error === UNSUPPORTED_SETTINGS_KEY_BODY &&
    typeof error.body.key === 'string'
  ) {
    return {
      state: 'protected-write-rejected',
      operation,
      reasonCode: 'unsupported-settings-key',
      message: 'This protected setting is not supported by the project gateway.',
    }
  }

  if (
    isWorkerRequestFailure(error) &&
    error.status === 409 &&
    isExactRecord(error.body, ['error', 'code']) &&
    error.body.error === 'Generation authority is immutable' &&
    error.body.code === GENERATION_AUTHORITY_IMMUTABLE_CODE
  ) {
    return {
      state: 'protected-write-rejected',
      operation,
      reasonCode: GENERATION_AUTHORITY_IMMUTABLE_CODE,
      message: 'This protected setting is immutable after project creation.',
    }
  }

  return {
    state: 'error',
    operation,
    message: 'Settings could not be saved.',
  }
}

export function publishStudioSettingsWriteOutcome(
  outcome: StudioSettingsWriteOutcome,
  attempt?: number,
): void {
  if (attempt !== undefined && attempt !== latestAttempt) return
  if (attempt === undefined) latestAttempt += 1
  snapshot = Object.freeze({ ...outcome }) as StudioSettingsWriteOutcome
  subscribers.forEach((subscriber) => subscriber())
}

export function beginStudioSettingsWriteOutcome(
  operation: StudioSettingsWriteOperation,
): number {
  latestAttempt += 1
  const attempt = latestAttempt
  snapshot = Object.freeze({ state: 'submitting', operation })
  subscribers.forEach((subscriber) => subscriber())
  return attempt
}

export function subscribeStudioSettingsWriteOutcome(
  subscriber: () => void,
): () => void {
  subscribers.add(subscriber)
  return () => subscribers.delete(subscriber)
}

export function getStudioSettingsWriteOutcome(): StudioSettingsWriteOutcome {
  return snapshot
}
